import React, { useState, useCallback } from 'react';
import { View, FlatList, RefreshControl, ScrollView, Alert, Platform } from 'react-native';
import { Button, FAB } from 'react-native-paper';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { isAdminOrSuperAgentRole } from '../redux/slices/authSlice';
import { fetchCommandes, deleteCommande } from '../redux/slices/commandesSlice';
import CommandeCard from '../components/CommandeCard';
import EmptyState from '../components/EmptyState';
import { FilterChip, PageHeader, SearchPill } from '../components/ui';
import { CONTENT_MAX_WIDTH, RADIUS } from '../assets/theme';
import { makeStyles, useAppTheme } from '../theme/ThemeContext';
import { useLayout } from '../theme/useLayout';
import { RootStackParamList } from '../navigation/AppNavigator';
import { StatutCommande } from '../types';

type Nav = StackNavigationProp<RootStackParamList, 'Main'>;

const FILTERS = [
  { value: 'TOUS', label: 'Tous' },
  { value: 'DEVIS', label: 'Devis' },
  { value: 'PAYEE', label: 'Payées' },
  { value: 'FACTURE', label: 'Facturés' },
  { value: 'BON_COMMANDE', label: 'Bons de commande' },
  { value: 'NON_PAYEE', label: 'Non payée' },
  { value: 'ECHEANCE_DEPASSEE', label: 'Echéance dépassée' },
] as const;

/** Horizontal gap between the two columns of the desktop grid. */
const GRID_GAP = 16;

export default function ListeCommandes() {
  const styles = useStyles();
  const { colors } = useAppTheme();
  const { isDesktop, gutter } = useLayout();
  const dispatch = useAppDispatch();
  const navigation = useNavigation<Nav>();
  const { list, loading } = useAppSelector((s) => s.commandes);
  const canManageAdminFeatures = useAppSelector((s) => isAdminOrSuperAgentRole(s.auth.role));
  const [filter, setFilter] = useState<'TOUS' | StatutCommande | 'NON_PAYEE' | 'ECHEANCE_DEPASSEE'>('TOUS');
  const [search, setSearch] = useState('');

  const load = useCallback(() => { dispatch(fetchCommandes()); }, [dispatch]);
  useFocusEffect(useCallback(() => {
    load();
  }, [load]));

  const filtered = list.filter((c) => {
    const verse = c.paiements.reduce((s, p) => s + Number(p.montant), 0);
    const todayIso = new Date().toISOString().slice(0, 10);
    let matchStatut: boolean;
    if (filter === 'TOUS') {
      matchStatut = true;
    } else if (filter === 'NON_PAYEE') {
      matchStatut = (c.statut === 'FACTURE' || c.statut === 'BON_COMMANDE')
        && (Number(c.totalTTC) - verse) > 0;
    } else if (filter === 'ECHEANCE_DEPASSEE') {
      matchStatut = (c.statut === 'FACTURE' || c.statut === 'BON_COMMANDE')
        && !!c.dateEcheance
        && c.dateEcheance < todayIso
        && (Number(c.totalTTC) - verse) > 0;
    } else {
      matchStatut = c.statut === filter;
    }
    const matchSearch =
      !search ||
      c.client?.nomRaisonSociale?.toLowerCase().includes(search.toLowerCase()) ||
      String(c.id).includes(search) ||
      (c.numeroFacture?.toLowerCase().includes(search.toLowerCase()) ?? false) ||
      (c.numeroBonCommande?.toLowerCase().includes(search.toLowerCase()) ?? false);
    return matchStatut && matchSearch;
  });

  const confirmAction = (message: string, onConfirm: () => void) => {
    if (Platform.OS === 'web') {
      const webGlobal = globalThis as unknown as { confirm?: (msg: string) => boolean };
      const confirmed = typeof webGlobal.confirm === 'function'
        ? webGlobal.confirm(message)
        : true;
      if (confirmed) onConfirm();
      return;
    }

    Alert.alert('Supprimer la commande', message, [
      { text: 'Annuler', style: 'cancel' },
      { text: 'Supprimer', style: 'destructive', onPress: onConfirm },
    ]);
  };

  const confirmDeleteCommande = (id: number, statut: StatutCommande) => {
    const typeLabel = statut === 'FACTURE'
      ? 'la facture'
      : statut === 'BON_COMMANDE'
        ? 'le bon de commande'
      : statut === 'PAYEE'
        ? 'la commande payée'
        : 'le devis';
    confirmAction(`Confirmer la suppression de ${typeLabel} #${id} ?`, async () => {
      try {
        await dispatch(deleteCommande(id)).unwrap();
      } catch (e: any) {
        const msg = typeof e === 'string' ? e : 'Impossible de supprimer cette commande.';
        Alert.alert('Erreur', msg);
      }
    });
  };

  const openNouvelleCommande = () => navigation.navigate('NouvelleCommande', {});

  // Desktop: two-column card grid. Changing numColumns requires a fresh FlatList (key).
  const numColumns = isDesktop ? 2 : 1;
  const column = { paddingHorizontal: gutter };
  const hasActiveCriteria = filter !== 'TOUS' || !!search;

  return (
    <View style={styles.container}>
      <View style={[styles.headerBlock, column, { paddingTop: isDesktop ? 32 : 16 }]}>
        <PageHeader
          title="Commandes"
          subtitle={`${filtered.length} résultat${filtered.length !== 1 ? 's' : ''}`}
          actions={isDesktop ? (
            <Button mode="contained" icon="plus" onPress={openNouvelleCommande} contentStyle={styles.primaryBtnContent}>
              Nouvelle commande
            </Button>
          ) : undefined}
        />

        <SearchPill
          placeholder="Client ou #ID..."
          value={search}
          onChangeText={setSearch}
          style={styles.search}
        />

        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          style={styles.chipScroll}
          contentContainerStyle={styles.chipRow}
        >
          {FILTERS.map((btn) => (
            <FilterChip
              key={btn.value}
              label={btn.label}
              selected={filter === btn.value}
              onPress={() => setFilter(btn.value)}
            />
          ))}
        </ScrollView>
      </View>

      <FlatList
        key={`cols-${numColumns}`}
        style={styles.listFlex}
        data={filtered}
        numColumns={numColumns}
        keyExtractor={(item) => String(item.id)}
        renderItem={({ item, index }) => (
          <View
            style={numColumns > 1
              ? [styles.gridCell, index % 2 === 0 ? { paddingRight: GRID_GAP / 2 } : { paddingLeft: GRID_GAP / 2 }]
              : undefined}
          >
            <CommandeCard
              commande={item}
              showEcheanceDepasseeFlag
              style={numColumns > 1 ? styles.gridCard : undefined}
              onPress={() => navigation.navigate('DetailCommande', { commandeId: item.id })}
              onDeleteCommande={(item.statut === 'DEVIS'
                || item.statut === 'PAYEE'
                || ((item.statut === 'FACTURE' || item.statut === 'BON_COMMANDE') && canManageAdminFeatures))
                ? () => confirmDeleteCommande(item.id, item.statut)
                : undefined}
            />
          </View>
        )}
        ListEmptyComponent={(
          <EmptyState
            icon="file-document-outline"
            message="Aucune commande trouvée"
            hint={hasActiveCriteria ? 'Essayez une autre recherche ou un autre filtre.' : undefined}
          />
        )}
        refreshControl={(
          <RefreshControl
            refreshing={loading}
            onRefresh={load}
            colors={[colors.primary]}
            tintColor={colors.primary}
            progressBackgroundColor={colors.surface}
          />
        )}
        contentContainerStyle={[styles.list, column, !isDesktop && styles.listWithFab]}
      />

      {!isDesktop && (
        <FAB
          icon="plus"
          style={styles.fab}
          color={colors.onPrimary}
          onPress={openNouvelleCommande}
          label="Nouvelle"
          accessibilityLabel="Nouvelle commande"
        />
      )}
    </View>
  );
}

const useStyles = makeStyles((c) => ({
  container: { flex: 1, backgroundColor: c.background },
  headerBlock: { width: '100%', maxWidth: CONTENT_MAX_WIDTH, alignSelf: 'center' },
  primaryBtnContent: { paddingHorizontal: 8, minHeight: 44 },
  search: { marginBottom: 12 },
  chipScroll: { flexGrow: 0, flexShrink: 0, marginBottom: 16 },
  chipRow: { flexDirection: 'row', alignItems: 'center', gap: 8, paddingVertical: 2 },
  listFlex: { flex: 1 },
  list: { width: '100%', maxWidth: CONTENT_MAX_WIDTH, alignSelf: 'center', paddingTop: 4, paddingBottom: 48 },
  listWithFab: { paddingBottom: 96 },
  gridCell: { flex: 1, maxWidth: '50%' },
  gridCard: { flex: 1 },
  fab: {
    position: 'absolute',
    bottom: 20,
    right: 20,
    backgroundColor: c.primary,
    borderRadius: RADIUS.pill,
  },
}));
